import React, { useEffect, useState } from 'react';
import { Card, DatePicker, Row, Col, message, Spin, Empty } from 'antd';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';
import { Users } from 'lucide-react';
import axios from '../../utils/axios';
import dayjs from 'dayjs';

const { RangePicker } = DatePicker;

const Reports = () => {
  const cafeId = localStorage.getItem('cafeId');
  const [dateRange, setDateRange] = useState([
    dayjs().startOf('day'),
    dayjs().endOf('day')
  ]);
  const [peopleCountData, setPeopleCountData] = useState([]);
  const [loadingPerson, setLoadingPerson] = useState(false);
  const [tableOccupancyData, setTableOccupancyData] = useState([]);
  const [loadingOccupancy, setLoadingOccupancy] = useState(false);
  const user = JSON.parse(localStorage.getItem('user'));
  const [reportConfig, setReportConfig] = useState(null);

  const fetchReportConfig = async () => {
    try {
      const res = await axios.post('/fetch-dashboard-settings', {
        cafeId,
        username: user?.username
      });

      if (res.data?.success) {
        setReportConfig(res.data.data.pages?.report);
      }
    } catch (err) {
      console.error("Report config fetch failed", err);
    }
  }

  useEffect(() => {
    fetchReportConfig();
  }, []);

  const REPORT_RENDERERS = {
    "People Count Analytics": {
      grid: "grid-cols-1",
      render: () => (
        <Card className="shadow-sm">
          <div className="flex items-center mb-4">
            <Users className="w-6 h-6 text-blue-600 mr-2" />
            <h2 className="text-xl font-semibold">People Count Analytics</h2>
          </div>

          {loadingPerson ? (
            <div className="flex justify-center items-center h-[350px]">
              <Spin size="large" />
            </div>
          ) : peopleCountData.length === 0 ? (
            <Empty description="No people count data" />
          ) : (
            <ResponsiveContainer width="100%" height={350}>
              <LineChart data={peopleCountData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="time" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Line
                  type="monotone"
                  dataKey="visitors"
                  stroke="#3b82f6"
                  strokeWidth={3}
                />
              </LineChart>
            </ResponsiveContainer>
          )}
        </Card>
      )
    },

    "Total Table Occupancy": {
      grid: "grid-cols-1",
      render: () => (
        <Card className="shadow-sm">
          <div className="flex items-center mb-4">
            <Users className="w-6 h-6 text-green-600 mr-2" />
            <h2 className="text-xl font-semibold">Total Table Occupancy</h2>
          </div>

          {loadingOccupancy ? (
            <div className="flex justify-center items-center h-[350px]">
              <Spin size="large" />
            </div>
          ) : tableOccupancyData.length === 0 ? (
            <Empty description="No occupancy data" />
          ) : (
            <ResponsiveContainer width="100%" height={350}>
              <LineChart data={tableOccupancyData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="time" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Line
                  type="monotone"
                  dataKey="occupancy"
                  stroke="#10b981"
                  strokeWidth={3}
                />
                <Line
                  type="monotone"
                  dataKey="capacity"
                  stroke="#ef4444"
                  strokeWidth={2}
                />
              </LineChart>
            </ResponsiveContainer>
          )}
        </Card>
      )
    }
  };

  const getReportGrid = (cards = []) => {
    if (cards.length === 1) return "grid-cols-1";
    if (cards.length === 2) return "grid-cols-2";
    return "grid-cols-1";
  };

  const fetchPersonCount = async (range) => {
    try {
      setLoadingPerson(true);
      const [start, end] = range;

      const res = await axios.post('/person-count', {
        cafeId,
        startDate: start.startOf('day').toISOString(),
        endDate: end.endOf('day').toISOString()
      });

      if (res.data?.success) {
        const chartData = res.data.data
          .sort((a, b) => new Date(a.createdOn) - new Date(b.createdOn))
          .map(item => ({
            time: dayjs(item.createdOn).format('HH:mm'),
            visitors: item.personCount
          }));

        setPeopleCountData(chartData);
      } else {
        setPeopleCountData([]);
      }
    } catch (error) {
      message.error('Failed to load people count data');
      setPeopleCountData([]);
    } finally {
      setLoadingPerson(false);
    }
  };

  /* =========================
      FETCH TOTAL OCCUPANCY
     ========================= */
  const fetchTotalOccupancy = async (range) => {
    try {
      setLoadingOccupancy(true);
      const [start, end] = range;

      const res = await axios.post('/total-occupancy', {
        cafeId,
        startDate: start.startOf('day').toISOString(),
        endDate: end.endOf('day').toISOString()
      });

      if (res.data?.success) {
        const chartData = res.data.data.map(item => ({
          time: dayjs(item.createdOn).format('HH:mm'),
          occupancy: item.occupancy,
          capacity: item.capacity
        }));

        setTableOccupancyData(chartData);
      } else {
        setTableOccupancyData([]);
      }
    } catch (error) {
      message.error('Failed to load table occupancy data');
      setTableOccupancyData([]);
    } finally {
      setLoadingOccupancy(false);
    }
  };

  /* =========================
      INITIAL LOAD
     ========================= */
  useEffect(() => {
    fetchPersonCount(dateRange);
    fetchTotalOccupancy(dateRange);
  }, []);

  /* =========================
      DATE CHANGE
     ========================= */
  useEffect(() => {
    if (dateRange) {
      fetchPersonCount(dateRange);
      fetchTotalOccupancy(dateRange);
    }
  }, [dateRange]);


  return (
    <div className="max-w-7xl mx-auto">

      {/* Header */}
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-800 mb-2">
          Analytics Reports
        </h1>
        <p className="text-gray-600">
          Monitor people count & table occupancy analytics
        </p>
      </div>

      {/* Filters */}
      <Card className="mb-6 shadow-sm">
        <Row gutter={16}>
          <Col xs={24} sm={12} lg={8}>
            <label className="block text-sm font-medium mb-2">
              Date Range
            </label>
            <RangePicker
              className="w-full"
              value={dateRange}
              allowClear={false}
              onChange={(dates) => setDateRange(dates)}
            />
          </Col>
        </Row>
      </Card>

      {reportConfig?.rows?.map((row, rowIdx) => (
        <div
          key={rowIdx}
          className={`grid ${getReportGrid(row.cards)} gap-6 mb-6`}
        >
          {row.cards.map((card, idx) => {
            const def = REPORT_RENDERERS[card.name];
            if (!def) return null;
            return <React.Fragment key={idx}>{def.render()}</React.Fragment>;
          })}
        </div>
      ))}


      {/* ================= PEOPLE COUNT ================= */}
      {/* <Card className="shadow-sm">
        <div className="flex items-center mb-4">
          <Users className="w-6 h-6 text-blue-600 mr-2" />
          <h2 className="text-xl font-semibold">People Count Analytics</h2>
        </div>

        {loadingPerson && (
          <div className="flex justify-center items-center h-[350px]">
            <Spin size="large" />
          </div>
        )}

        {!loadingPerson && peopleCountData.length === 0 && (
          <div className="flex justify-center items-center h-[350px]">
            <Empty description="No people count data for selected date range" />
          </div>
        )}

        {!loadingPerson && peopleCountData.length > 0 && (
          <ResponsiveContainer width="100%" height={350}>
            <LineChart data={peopleCountData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="time" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Line
                type="monotone"
                dataKey="visitors"
                name="Visitor Count"
                stroke="#3b82f6"
                strokeWidth={3}
                dot={{ r: 4 }}
              />
            </LineChart>
          </ResponsiveContainer>
        )}
      </Card> */}

      {/* ================= TOTAL TABLE OCCUPANCY ================= */}
      {/* <Card className="shadow-sm mt-6">
        <div className="flex items-center mb-4">
          <Users className="w-6 h-6 text-green-600 mr-2" />
          <h2 className="text-xl font-semibold">Total Table Occupancy</h2>
        </div>

        {loadingOccupancy && (
          <div className="flex justify-center items-center h-[350px]">
            <Spin size="large" />
          </div>
        )}

        {!loadingOccupancy && tableOccupancyData.length === 0 && (
          <div className="flex justify-center items-center h-[350px]">
            <Empty description="No table occupancy data for selected date range" />
          </div>
        )}

        {!loadingOccupancy && tableOccupancyData.length > 0 && (
          <ResponsiveContainer width="100%" height={350}>
            <LineChart data={tableOccupancyData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="time" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Line
                type="monotone"
                dataKey="occupancy"
                name="Occupied Seats"
                stroke="#10b981"
                strokeWidth={3}
              />
              <Line
                type="monotone"
                dataKey="capacity"
                name="Total Capacity"
                stroke="#ef4444"
                strokeWidth={2}
              />
            </LineChart>
          </ResponsiveContainer>
        )}
      </Card> */}

    </div>
  );
};

export default Reports;